import React from 'react';

interface SchemaMarkupProps {
  type: 'organization' | 'localBusiness' | 'service' | 'review' | 'article' | 'website' | 'faqPage';
  data?: any;
}

export const SchemaMarkup: React.FC<SchemaMarkupProps> = ({ type, data }) => {
  const getSchema = () => {
    switch (type) {
      case 'organization':
        return {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Hudson My Home",
          "alternateName": "The Hudson Club",
          "description": "A full maintenance membership and home maintenance membership for discerning homeowners who want every detail managed — before they even think of it.",
          "url": "https://hudsonmyhome.com",
          "logo": "https://hudsonmyhome.com/logo.png",
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-515-216-4433",
            "contactType": "customer service",
            "email": "info@hudsonmyhome.com",
            "areaServed": "Des Moines Metro Area",
            "availableLanguage": "English"
          },
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Urbandale",
            "addressRegion": "Iowa",
            "addressCountry": "US"
          },
          "sameAs": [
            "https://hudsonmyhome.com"
          ],
          "foundingDate": "2024",
          "slogan": "Where Homes Are Masterpieces — And Owners Are Free"
        };

      case 'localBusiness':
        return {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Hudson My Home",
          "description": "Private home management for homeowners who value their time. We maintain your home so you don't have to.",
          "url": "https://hudsonmyhome.com",
          "telephone": "+1-515-216-4433",
          "email": "info@hudsonmyhome.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Urbandale",
            "addressLocality": "Urbandale",
            "addressRegion": "IA",
            "postalCode": "50323",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "41.5868",
            "longitude": "-93.6250"
          },
          "areaServed": [
            { "@type": "City", "name": "Ankeny" },
            { "@type": "City", "name": "West Des Moines" },
            { "@type": "City", "name": "Waukee" },
            { "@type": "City", "name": "Johnston" },
            { "@type": "City", "name": "Urbandale" },
            { "@type": "City", "name": "Altoona" },
            { "@type": "City", "name": "Ames" },
            { "@type": "City", "name": "Des Moines" }
          ],
          "sameAs": [
            "https://www.instagram.com/hudsonmyhome/",
            "https://www.facebook.com/hudsonmyhome",
            "https://www.linkedin.com/company/hudson-my-home/"
          ],
          "serviceArea": {
            "@type": "GeoCircle",
            "geoMidpoint": {
              "@type": "GeoCoordinates",
              "latitude": "41.5868",
              "longitude": "-93.6250"
            },
            "geoRadius": "50000"
          },
          "openingHours": "Mo-Fr 08:00-18:00",
          "priceRange": "$$$",
          "paymentAccepted": "Cash, Credit Card, Check",
          "currenciesAccepted": "USD"
        };

      case 'service':
        return {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Hudson My Home",
          "description": "Proactive home management with a dedicated home professional, year-round maintenance, vendor coordination, and 24/7 priority support.",
          "provider": {
            "@type": "Organization",
            "name": "Hudson My Home",
            "url": "https://hudsonmyhome.com"
          },
          "serviceType": "Home Management",
          "areaServed": [
            { "@type": "City", "name": "Ankeny" },
            { "@type": "City", "name": "West Des Moines" },
            { "@type": "City", "name": "Waukee" },
            { "@type": "City", "name": "Johnston" },
            { "@type": "City", "name": "Urbandale" },
            { "@type": "City", "name": "Altoona" },
            { "@type": "City", "name": "Ames" },
            { "@type": "City", "name": "Des Moines" }
          ],
          "offers": [
            {
              "@type": "Offer",
              "name": "Estate Management (<1500 SqFt)",
              "price": "399",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "399",
                "priceCurrency": "USD",
                "unitText": "per month"
              },
              "description": "Complete oversight of your property — proactive maintenance and emergency coordination for homes under 1500 square feet."
            },
            {
              "@type": "Offer",
              "name": "Estate Management (1501-2999 SqFt)",
              "price": "449",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "449",
                "priceCurrency": "USD",
                "unitText": "per month"
              },
              "description": "Complete oversight of your property — proactive maintenance and emergency coordination for homes 1501-2999 square feet."
            },
            {
              "@type": "Offer",
              "name": "Estate Management (3000-4500 SqFt)",
              "price": "549",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "549",
                "priceCurrency": "USD",
                "unitText": "per month"
              },
              "description": "Complete oversight of your property — proactive maintenance and emergency coordination for homes 3000-4500 square feet."
            }
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Hudson Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Proactive Maintenance",
                  "description": "Monthly home inspections and preventive maintenance"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Concierge Services",
                  "description": "White-glove, anticipatory service that handles every detail"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Long-View Planning",
                  "description": "12-month proactive maintenance and 5-year blueprint planning"
                }
              }
            ]
          }
        };

      case 'website':
        return {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Hudson My Home",
          "alternateName": "The Hudson Club",
          "url": "https://hudsonmyhome.com",
          "description": "A full maintenance membership and home maintenance membership for discerning homeowners who want every detail managed — before they even think of it.",
          "publisher": {
            "@type": "Organization",
            "name": "Hudson My Home",
            "url": "https://hudsonmyhome.com"
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://hudsonmyhome.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          },
          "inLanguage": "en-US"
        };

      case 'faqPage':
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What makes Hudson different from hiring a handyman?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Hudson isn't about fixing things — it's about preventing them. Your Hudson learns your home intimately, visits on a regular cadence to catch issues early, vets all vendors, oversees all work, and ensures you're never overcharged."
              }
            },
            {
              "@type": "Question",
              "name": "What's included in membership?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "A dedicated Hudson professional assigned specifically to your home, monthly in-person visits, proactive maintenance oversight, vendor vetting and oversight, 24/7 priority support, and our satisfaction guarantee."
              }
            },
            {
              "@type": "Question",
              "name": "Can I try membership before committing?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. We offer a 30-day satisfaction guarantee. If you don't see the value within your first month, we'll refund your membership fee entirely."
              }
            }
          ]
        };

      default:
        return data || {};
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(getSchema(), null, 2)
      }}
    />
  );
};

export default SchemaMarkup;
